import moment from 'moment';

import { Section, SECTION_SLA_BUSINESS_HOURS } from '../../../../Model/CoachNotificationView';
import { getElapsedBusinessHours } from '../../../../utils/date';
import { checkIsPodScoped, getSectionPodData } from '../../../utils/coachSectionPods';

import texts from '../texts.json';

/** Sections in the order they are read, matching the coach dashboard's columns. */
const SECTION_ORDER = Object.values(Section);

/** How an AC is doing against their SLAs. */
const CompletionStatus = {
  CLEAR: 'CLEAR',
  AT_RISK: 'AT_RISK',
  BEHIND: 'BEHIND',
};

/** The two kinds of row: a coach row belongs to the AC row above it. */
const ACLeadRowType = {
  AC: 'AC',
  COACH: 'COACH',
};

/** How badly a queue has blown its SLA. */
const OverdueSeverity = {
  BREACHED: 'BREACHED',
  CRITICAL: 'CRITICAL',
};

/**
 * What one cell of the task matrix has to say, which is not always a number.
 *
 * `NONE` and `WITHIN_SLA` are deliberately distinct: an empty queue and a queue with time left both
 * read as absence today, and only one of them is work the lead will see again.
 */
const TaskCellState = {
  /** Nothing pending at all. */
  NONE: 'NONE',
  /** Pending, none of it past due yet. */
  WITHIN_SLA: 'WITHIN_SLA',
  /** Pending, but the age of the oldest item is not currently known — see `hasKnownAge`. */
  UNKNOWN_AGE: 'UNKNOWN_AGE',
  /** Past due, but the threshold or the suspect filter has taken it out of scope. */
  EXCLUDED: 'EXCLUDED',
  /** Past due by so much that it wants auditing rather than chasing. */
  SUSPECT: 'SUSPECT',
  /** Past due, inside its own allowance again. */
  BREACHED: 'BREACHED',
  /** Past due by at least a second full allowance. */
  CRITICAL: 'CRITICAL',
};

/**
 * How far past its SLA a queue must be before the dashboard calls it past due.
 *
 * Layered on top of the per-section SLAs rather than replacing them: the SLAs differ by section —
 * 24 business-day hours for messages, 48 for check-ins — and a single flat age would flatten that
 * distinction away. Zero means anything that has breached at all.
 *
 * Nothing under twelve hours is offered: the AC lead asked for the sub-12h options to go, because
 * almost everything here carries a 24-hour deadline and a threshold below half of that never
 * separated anything. On the current backlog the whole range moves under 1% of the items — the
 * control that actually changes what a lead sees is the suspect band below.
 */
const PAST_DUE_THRESHOLD_HOURS = [0, 12, 24, 48, 168];

/** A business day is 24 business-day hours, matching how the SLAs are expressed. */
const BUSINESS_HOURS_PER_DAY = 24;

/**
 * How far past SLA an item has to be before it stops being something to chase.
 *
 * Thirty business days. The AC lead's question was whether the year-old items on this dashboard are
 * still real, and the honest answer is that nobody knows yet — so rather than guess, anything this
 * far past its allowance is banded out of the red and labelled for auditing. It keeps its place in
 * the totals; it just stops competing for attention with the breaches from this week.
 */
const SUSPECT_THRESHOLD_BUSINESS_HOURS = 30 * BUSINESS_HOURS_PER_DAY;

/** The same band in whole business days, for the copy that has to name it. */
const SUSPECT_THRESHOLD_DAYS = SUSPECT_THRESHOLD_BUSINESS_HOURS / BUSINESS_HOURS_PER_DAY;

/** Where each section's pending work lives on the coach dashboard. */
const SECTION_PATHS = {
  [Section.CHATS]: (coachId) => `/c/${coachId}/chat`,
  [Section.CHECKINS]: (coachId) => `/c/${coachId}/feed?type=checkins`,
  [Section.WORKOUTS]: (coachId) => `/c/${coachId}/feed?type=workouts`,
  [Section.MEAL_PLANS]: (coachId) => `/c/${coachId}/feed?type=mealplans`,
  [Section.VIDEO_FEEDBACK]: (coachId) => `/c/${coachId}/feed?type=feedback`,
  [Section.REMINDERS]: (coachId) => `/c/${coachId}/feed?type=special`,
  [Section.ROUTING]: (coachId) => `/c/${coachId}/chat`,
};

/** Deep link to where a section's pending work is worked, used by the row links and Slack rundown. */
const getSectionPath = (section, coachId) => SECTION_PATHS[section](coachId);

/**
 * Timestamp a section's SLA clock runs from.
 *
 * Routing records when it was last flagged rather than the oldest item waiting, which under-reports
 * a backlog rather than over-reporting it — so it never raises a breach that is not real.
 */
const getSectionPendingSince = (section, sectionData) => (
  section === Section.ROUTING ? sectionData?.lastFlaggedAt : sectionData?.oldestNotificationAt
);

/** Business-day hours a section is past its SLA. Zero or less means it is still within it. */
const getSectionOverdueBusinessHours = (pendingSince, section, now = moment()) => (
  getElapsedBusinessHours(moment.unix(pendingSince.seconds), now) - SECTION_SLA_BUSINESS_HOURS[section]
);

/**
 * Grades a breach as a fraction of the section's own allowance rather than a fixed number of hours:
 * eight hours late on a 48-hour check-in is a third of the way through its allowance, and eight
 * hours late on a 24-hour message is two thirds.
 */
const getOverdueSeverity = (overdueBusinessHours, section) => (
  overdueBusinessHours >= SECTION_SLA_BUSINESS_HOURS[section]
    ? OverdueSeverity.CRITICAL
    : OverdueSeverity.BREACHED
);

/**
 * Renders overdue business-day hours as days and hours, so a long wait reads "1d 8h" rather than
 * an unreadable "32h". Days are business days: weekends never count towards them.
 */
const formatOverdueDuration = (overdueBusinessHours) => {
  const wholeHours = Math.floor(overdueBusinessHours);

  if (wholeHours < 1) {
    return texts.overdueLessThanHour;
  }

  const days = Math.floor(wholeHours / BUSINESS_HOURS_PER_DAY);
  const hours = wholeHours % BUSINESS_HOURS_PER_DAY;

  if (!days) {
    return `${hours}h`;
  }

  return hours ? `${days}d ${hours}h` : `${days}d`;
};

/**
 * Any count this dashboard prints, with thousand separators — the backlogs run into four figures.
 *
 * Not called `formatCount`: `utils/formatters` already exports that name meaning something else
 * entirely (zero-padding below ten).
 */
const formatDashboardCount = (count) => count.toLocaleString();

/** Renders a threshold as the short label its button shows — 12h, 1d, 7d. */
const formatThresholdLabel = (hours) => (
  hours % BUSINESS_HOURS_PER_DAY === 0 ? `${hours / BUSINESS_HOURS_PER_DAY}d` : `${hours}h`
);

/**
 * The pending work of one section as it applies to an AC on a given coach.
 *
 * An AC assigned to a pod answers only for that pod, so a pod with nothing pending yields nothing
 * at all — never the coach total, which would put another pod's work under this AC's name.
 * Everything else falls back to the coach total: an AC with no pod there works the whole book
 * alongside the other ACs, and routing has no pod to be split by.
 */
const getACSectionShare = (section, sectionData, podId) => {
  const podShare = getSectionPodData(section, sectionData, podId);

  if (podShare) {
    return {
      pendingCount: podShare.unreadCount ?? 0,
      overdueCount: podShare.overdueCount ?? 0,
      pendingSince: podShare.oldestNotificationAt,
    };
  }

  // The same predicate the AC Dashboard narrows its cells by, so the two dashboards cannot drift
  // over which sections a pod can be held accountable for.
  return checkIsPodScoped({ section, sectionData, podId })
    ? undefined
    : {
      pendingCount: sectionData?.unreadCount ?? 0,
      overdueCount: sectionData?.overdueCount ?? 0,
      pendingSince: getSectionPendingSince(section, sectionData),
    };
};

/**
 * How many of a section's pending items to report as past due.
 *
 * The count is the server's. It is kept live as work arrives and clears, and fully recomputed once
 * a day — items cross their SLA through the passage of time, which nothing writes a document for.
 * The oldest item's own timestamp, by contrast, is always current.
 *
 * So the two can disagree for a while, and the reconciliation goes both ways. If the timestamp says
 * a queue has breached, at least one item is past due whatever the count says — reporting zero
 * beside a queue the dashboard is calling past due would be plainly wrong. And the count can never
 * exceed what is actually pending, so it cannot claim more overdue items than there are items.
 */
const getReportedOverdueCount = ({
  section, overdueCount, pendingCount, isPastDue,
}) => {
  // Routing is one coach-level flag rather than a queue, so it has no count of its own: the whole
  // flag is a single overdue item once it has breached.
  if (section === Section.ROUTING) {
    return Number(isPastDue);
  }

  return Math.min(Math.max(overdueCount, Number(isPastDue)), pendingCount);
};

/**
 * The coaches whose AC assignments overlap, so the rows reporting them twice can say so.
 *
 * Sharing a coach is not by itself a double count — what matters is the scope each assignment
 * reads. An assignment carrying a pod is served that pod's own counters (see `getACSectionShare`),
 * so two ACs on distinct pods read disjoint numbers and neither over-reports. An assignment with no
 * pod falls back to the coach's whole book, which already contains every pod's work — so the moment
 * one unscoped assignment sits alongside any other, the same items are reported on both rows.
 *
 * Hence the rule: more than one AC on the coach, and at least one of them unscoped. Flagging on the
 * AC count alone put a warning on every properly pod-split coach, claiming a double count that was
 * not happening.
 *
 * One nuance this deliberately does not chase: routing is never pod-scoped, so two pod-holding ACs
 * do overlap on that one section. It is a coach-level flag worth at most a single item, and
 * flagging every pod-split coach for it would undo the point of this check.
 */
const buildOverlappingCoachIds = (acAssignments) => {
  const scopesByCoachId = acAssignments.reduce((byCoachId, { userId, coach, podId }) => {
    if (!userId || !coach) {
      return byCoachId;
    }

    const scopes = byCoachId.get(coach) ?? [];

    return byCoachId.set(coach, [...scopes, { userId, podId }]);
  }, new Map());

  return new Set(
    [...scopesByCoachId.entries()]
      .filter(([, scopes]) => (
        new Set(scopes.map(({ userId }) => userId)).size > 1
        && scopes.some(({ podId }) => !podId)
      ))
      .map(([coachId]) => coachId),
  );
};

/**
 * Every queue an AC is accountable for — one entry per (AC, coach, section) with anything pending,
 * whether or not it has breached its SLA. An AC holds at most one pod on a coach, so each
 * assignment is one scope.
 *
 * Queues within SLA are kept rather than filtered out, because the dashboard reports an AC's total
 * unread alongside what is past due; dropping them would leave the total unknowable.
 *
 * An assignment with nothing pending anywhere still yields one empty entry, so that the roster
 * holds every AC and every coach they answer for rather than only the ones with work outstanding.
 *
 * @param {Array} notificationViews - CoachNotificationView docs.
 * @param {Array} acAssignments - Current internal assignments for the AC role.
 * @param {Map} [acSlackIdById] - Slack member id per AC, used by the chase action.
 * @param {Map} [podNamesById] - Pod name per pod id, used to label the rows.
 * @returns {Array} Queue entries.
 */
const buildACQueueEntries = (notificationViews, acAssignments, acSlackIdById = new Map(), podNamesById = new Map()) => {
  const viewsByCoachId = new Map(notificationViews.map((view) => [view.id, view]));
  /*
   * Resolved over every assignment rather than per AC, because an overlapping coach is only visible
   * from outside the row that reports them. Marked on the entries so the flag survives the AC filter
   * — worked out later, from the filtered set, it would vanish the moment a lead narrowed to one AC.
   */
  const overlappingCoachIds = buildOverlappingCoachIds(acAssignments);
  // Every entry is aged against the same instant, so the ordering stays consistent.
  const now = moment();

  return acAssignments.flatMap(({
    userId: acId, userName: acName, coach: coachId, podId,
  }) => {
    const view = viewsByCoachId.get(coachId);

    if (!acId || !coachId || !view) {
      return [];
    }

    const assignment = {
      acId,
      acName: acName || acId,
      acSlackId: acSlackIdById.get(acId),
      coachId,
      coachName: view.name ?? '',
      isSharedCoach: overlappingCoachIds.has(coachId),
    };

    const sectionEntries = SECTION_ORDER.flatMap((section) => {
      const sectionData = view[section];
      const {
        pendingCount = 0,
        overdueCount = 0,
        pendingSince,
      } = getACSectionShare(section, sectionData, podId) ?? {};

      if (!pendingCount) {
        return [];
      }

      /*
       * A pod's counts and its oldest item come from the same scheduled recompute, so they normally
       * agree — but an item whose own timestamp is missing leaves a pod holding pending work whose
       * age is not known.
       *
       * The queue still belongs on the dashboard: it keeps its place with an UNKNOWN_AGE cell, and
       * the tooltip says the age is coming. What it does not do is count towards `Past due`. There
       * is no answer to "how far past SLA is this", so grading it against any threshold — including
       * `Any` — would be inventing one, and it would break the arithmetic the task columns exist to
       * close. Today these queues are counted at `Any` and then vanish silently at every threshold
       * above it, which is the worse of the two.
       */
      const hasKnownAge = !!pendingSince;
      const overdueBusinessHours = hasKnownAge
        ? getSectionOverdueBusinessHours(pendingSince, section, now)
        : 0;
      const isPastDue = hasKnownAge && overdueBusinessHours > 0;

      return [{
        ...assignment,
        id: `${acId}-${coachId}-${section}`,
        // Routing cannot be split by pod, so it stays unlabelled even for a podded AC.
        podName: podId && section !== Section.ROUTING ? podNamesById.get(podId) ?? podId : '',
        section,
        taskName: texts.sections[section],
        pendingCount,
        overdueCount: getReportedOverdueCount({
          section, overdueCount, pendingCount, isPastDue,
        }),
        pendingSince,
        hasKnownAge,
        overdueBusinessHours,
        isPastDue,
        isSuspect: isPastDue && overdueBusinessHours > SUSPECT_THRESHOLD_BUSINESS_HOURS,
        // Set by applyPastDueFilters, so an entry always carries the field whether filtered or not.
        isExcluded: false,
        path: getSectionPath(section, coachId),
      }];
    });

    if (sectionEntries.length > 0) {
      return sectionEntries;
    }

    /*
     * Nothing pending on this coach. The assignment is still carried through as an empty queue so
     * that the roster reflects who holds what: an AC who is completely clear keeps their row, and
     * an AC's coach count covers every coach they answer for rather than only the busy ones.
     */
    return [{
      ...assignment,
      id: `${acId}-${coachId}-clear`,
      podName: podId ? podNamesById.get(podId) ?? podId : '',
      section: undefined,
      taskName: '',
      pendingCount: 0,
      overdueCount: 0,
      pendingSince: undefined,
      hasKnownAge: true,
      overdueBusinessHours: 0,
      isPastDue: false,
      isSuspect: false,
      isExcluded: false,
      path: `/c/${coachId}`,
    }];
  });
};

/**
 * Narrows what counts as past due to whatever the lead has left in scope.
 *
 * Two controls, applied together because they answer the same question from opposite ends: the
 * threshold hides breaches that are too fresh to chase, and the suspect switch hides the ones too
 * old to trust.
 *
 * Entries are re-flagged rather than dropped, twice over. An AC's total unread covers every queue
 * they hold, so filtering here would take the queue out of that total too; and a queue the lead has
 * just excluded is worth showing as excluded — struck through, with its count still legible — so
 * they can see the mass they took out of scope rather than watching rows disappear.
 *
 * A queue whose age is unknown was never past due to begin with (see `buildACQueueEntries`), so
 * neither control has anything to say about it.
 *
 * The filters move the flag, not the count the entry carries. A queue is admitted on the age of its
 * **oldest** item — the only age the server records — and then contributes its whole `overdueCount`
 * to the AC's `Past due`. So under a non-zero threshold that number answers "how many overdue items
 * sit in queues that are this far behind", not "how many items are individually this far behind":
 * ten overdue check-ins whose oldest is eight days late all count under `7d`. This is deliberate.
 * The server writes one aggregate count per section with no age distribution, so the only
 * alternative is to report the single item whose age is known, which would undercount every
 * genuinely deep backlog — the thing a lead most needs to see.
 *
 * @param {Array} queueEntries - Queue entries from buildACQueueEntries.
 * @param {object} [filters] - Filters.
 * @param {number} [filters.thresholdHours] - Business hours past SLA a queue must exceed.
 * @param {boolean} [filters.hideSuspect] - Whether to take the suspect band out of scope.
 * @returns {Array} The same entries, re-flagged.
 */
const applyPastDueFilters = (queueEntries, { thresholdHours = 0, hideSuspect = false } = {}) => (
  queueEntries.map((entry) => {
    const isExcluded = entry.isPastDue && (
      entry.overdueBusinessHours <= thresholdHours || (hideSuspect && entry.isSuspect)
    );

    return isExcluded ? { ...entry, isPastDue: false, isExcluded: true } : entry;
  })
);

/**
 * The distinct ACs to offer in the filter, labelled so two people with the same first name can be
 * told apart.
 *
 * Narrowed to the people a lead actually chases: holders of the AC role, minus the AC leads
 * themselves (see `useACLeadData`). The roster comes from internal assignments, and leads, coaches
 * and admins hold AC assignments too — so the unfiltered list offered the lead a dozen names that
 * were never theirs to chase. Filtering by role fixes that at the source rather than by naming the
 * individuals, which would go stale with the next staffing change.
 *
 * Options only, not the roster: the excluded assignments are the AC of record for real coaches, so
 * their rows stay in the table. Dropping them would hide real backlog.
 *
 * The names come from the same place, because an assignment carries a first name only and there are
 * two Felipes and two Nicoles.
 *
 * An empty map means the AC user read failed rather than that nobody qualifies — that read is
 * a best-effort decoration this dashboard is designed to survive (see `useACLeadData`) — so the
 * filter falls back to every assignment, labelled as before. A degraded filter beats an empty one.
 * Only a settled read is ever seen here: the page holds the filter back until the decorations pass
 * has finished, so an empty map is never merely one that has not arrived yet.
 *
 * One AC works several coaches, so their assignments collapse to one option.
 *
 * @param {Array} acAssignments - Current internal assignments for the AC role.
 * @param {Map} [selectableACNamesById] - Full name per AC the filter should offer.
 * @returns {Array} `{ value, label }` options, by label.
 */
const buildACFilterOptions = (acAssignments, selectableACNamesById = new Map()) => {
  const isRoleKnown = selectableACNamesById.size > 0;

  const namesByACId = acAssignments.reduce((names, { userId, userName }) => {
    if (!userId || (isRoleKnown && !selectableACNamesById.has(userId))) {
      return names;
    }

    return names.set(userId, selectableACNamesById.get(userId) || userName || userId);
  }, new Map());

  return [...namesByACId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Grades an AC on severity rather than volume: one queue at twice its allowance is a worse state to
 * be in than five that have only just tipped over, and it is the one a lead should look at first.
 *
 * The suspect band is graded but never critical. An item thirty business days past its allowance
 * says nothing about how the AC is working this week, and letting one turn the whole row red is how
 * a roster ends up entirely red and therefore unreadable.
 */
const getCompletionStatus = (pastDueEntries) => {
  if (pastDueEntries.length === 0) {
    return CompletionStatus.CLEAR;
  }

  const hasCritical = pastDueEntries.some(({ overdueBusinessHours, section, isSuspect }) => (
    !isSuspect && getOverdueSeverity(overdueBusinessHours, section) === OverdueSeverity.CRITICAL
  ));

  return hasCritical ? CompletionStatus.BEHIND : CompletionStatus.AT_RISK;
};

/**
 * The parts of one queue a cell borrows: where it links, whose queue it is, how far past SLA it is
 * and since when.
 *
 * Picked out rather than spread, because an entry and a cell share field names that mean different
 * things — an entry's `pendingCount` is its own, while a cell's is the sum across every queue it
 * covers, and spreading would silently replace the second with the first on any AC row spanning
 * more than one coach.
 */
const describeQueue = (queueEntry) => ({
  coachName: queueEntry.coachName,
  path: queueEntry.path,
  pendingSince: queueEntry.pendingSince,
  overdueBusinessHours: queueEntry.overdueBusinessHours,
});

/** The entry with the longest wait past SLA, which is the one a row reports and links at. */
const getWorstOverdueEntry = (entries) => entries.reduce((worst, entry) => (
  entry.overdueBusinessHours > (worst?.overdueBusinessHours ?? -1) ? entry : worst
), undefined);

/**
 * The entry whose oldest item has waited longest, for cells with nothing past due to rank by.
 * A queue with no timestamp never wins, so it cannot stand in for a dated one.
 */
const getOldestPendingEntry = (entries) => entries.reduce((oldest, entry) => {
  if (!entry.pendingSince) {
    return oldest;
  }

  return entry.pendingSince.seconds < (oldest?.pendingSince?.seconds ?? Infinity) ? entry : oldest;
}, undefined) ?? entries[0];

/** Sums a field over entries. */
const sumBy = (entries, field) => entries.reduce((total, entry) => total + entry[field], 0);

/**
 * How a cell reads once something in it is past due, graded off the worst queue in scope.
 *
 * Suspect wins over critical: an item thirty business days past its allowance is not a harder chase
 * than one at twice its allowance, it is a different job — so it is banded out of the red rather
 * than shown as the most urgent thing on the row.
 */
const getPastDueCellState = (worst, section) => {
  if (worst.isSuspect) {
    return TaskCellState.SUSPECT;
  }

  return getOverdueSeverity(worst.overdueBusinessHours, section) === OverdueSeverity.CRITICAL
    ? TaskCellState.CRITICAL
    : TaskCellState.BREACHED;
};

/**
 * One cell of the task matrix: what a section's past-due work looks like across whatever queues the
 * row covers — a single queue on a coach row, one per coach on an AC row.
 *
 * The cell carries the **past-due** count, not the pending one, so the counted cells add up to the
 * row's own `Past due`. That is the whole point of the change: the stacked list this replaced showed
 * pending counts beside a past-due total, two units in one row, and the arithmetic never closed —
 * which is exactly the doubt about these numbers the AC lead raised.
 *
 * `count` is the number the cell shows and `isCounted` is whether it is part of that total, because
 * two states show a number they do not contribute: an excluded cell shows its count struck through
 * so the lead can see the mass they just took out of scope, and a queue of unknown age shows no
 * number at all. Anything summing the row must filter on `isCounted` rather than on the state.
 *
 * The state is graded from the **worst** contributing queue, and the count is everything in scope.
 * Aggregating after the filters rather than before is what keeps an AC row equal to the sum of its
 * own coach rows: grading a rolled-up total by its worst age would let one coach's suspect backlog
 * take a second coach's fresh breach out of scope with it.
 *
 * @param {string} section - The Section this column stands for.
 * @param {Array} entries - That section's entries for this row, already filtered.
 * @returns {object} The cell.
 */
const buildTaskCell = (section, entries) => {
  const cell = {
    section,
    sectionName: texts.sections[section],
    pendingCount: sumBy(entries, 'pendingCount'),
    isCounted: true,
  };

  if (!cell.pendingCount) {
    return { ...cell, state: TaskCellState.NONE, count: 0 };
  }

  const inScope = entries.filter(({ isPastDue }) => isPastDue);

  if (inScope.length > 0) {
    const worst = getWorstOverdueEntry(inScope);

    return {
      ...cell,
      ...describeQueue(worst),
      state: getPastDueCellState(worst, section),
      count: sumBy(inScope, 'overdueCount'),
    };
  }

  const excluded = entries.filter(({ isExcluded }) => isExcluded);

  if (excluded.length > 0) {
    return {
      ...cell,
      ...describeQueue(getWorstOverdueEntry(excluded)),
      state: TaskCellState.EXCLUDED,
      count: sumBy(excluded, 'overdueCount'),
      isCounted: false,
    };
  }

  /*
   * Pending work whose age nothing currently knows. It has no count to report, hence no `count`.
   * Described by a queue that is actually undated: on an AC row spanning several coaches the first
   * entry may be a perfectly well-dated one, and the cell would then link at — and name — a coach
   * with nothing unknown about them.
   */
  const undated = entries.find(({ hasKnownAge }) => !hasKnownAge);

  if (undated) {
    return {
      ...cell,
      ...describeQueue(undated),
      state: TaskCellState.UNKNOWN_AGE,
      count: undefined,
      isCounted: false,
    };
  }

  /*
   * Nothing is late here, so there is no worst queue to point at. The longest-waiting one is the
   * nearest thing to it and the one a lead would open first, which beats whichever entry happened
   * to be built first.
   */
  return {
    ...cell,
    ...describeQueue(getOldestPendingEntry(entries)),
    state: TaskCellState.WITHIN_SLA,
    count: 0,
  };
};

/** Every section's cell for one row, in column order, so a section is never a column that vanishes. */
const buildTaskCells = (entries) => {
  const entriesBySection = entries.reduce((bySection, entry) => {
    if (!entry.section) {
      return bySection;
    }

    const forSection = bySection.get(entry.section) ?? [];
    forSection.push(entry);

    return bySection.set(entry.section, forSection);
  }, new Map());

  return SECTION_ORDER.map((section) => buildTaskCell(section, entriesBySection.get(section) ?? []));
};

/**
 * What a row reports about its worst wait: how far past SLA it is, which section that was, and when
 * the item started waiting.
 *
 * Past SLA, not item age — those are two different clocks, and showing one while filtering on the
 * other is what made a row read `5y` beside a threshold grading it in business hours past a
 * 24-hour allowance. The absolute date and the item's total age move to the tooltip, where they
 * answer "since when" without being mistaken for "how late".
 *
 * All three come from the same entry, so the duration is never graded against another item's
 * allowance.
 */
const buildWorstOverdue = (pastDueEntries) => {
  const worst = getWorstOverdueEntry(pastDueEntries);

  return {
    worstOverdueHours: worst?.overdueBusinessHours ?? 0,
    worstOverdueSince: worst?.pendingSince,
    worstSection: worst?.section,
    // Taken from the same entry as the rest, so the cell links at the item it describes.
    worstPath: worst?.path,
    hasWorstOverdue: !!worst,
  };
};

/**
 * The coaches this AC answers for whose numbers overlap another AC's.
 *
 * At least one of the assignments is not pod-scoped, so it reports the coach's whole book and the
 * same items are reported — and counted — twice (see `buildOverlappingCoachIds`). Surfaced on the
 * row rather than quietly corrected, because which AC should own the work is a staffing question
 * the dashboard cannot answer.
 */
const getSharedCoachNames = (entries) => [
  ...new Set(entries.filter((entry) => entry.isSharedCoach).map(({ coachName }) => coachName)),
];

/** Collapses an AC's queue entries into the per-coach breakdown shown when they are expanded. */
const buildCoachBreakdown = (entries) => {
  const byCoachId = entries.reduce((breakdown, entry) => {
    const line = breakdown.get(entry.coachId) ?? {
      coachId: entry.coachId,
      coachName: entry.coachName,
      podName: entry.podName,
      isSharedCoach: entry.isSharedCoach,
      entries: [],
    };

    // Routing carries no pod, so the label comes from whichever section does.
    line.podName = line.podName || entry.podName;
    line.entries.push(entry);

    return breakdown.set(entry.coachId, line);
  }, new Map());

  return [...byCoachId.values()]
    .map(({ entries: coachEntries, ...line }) => {
      const pastDueEntries = coachEntries.filter(({ isPastDue }) => isPastDue);

      return {
        ...line,
        totalUnread: sumBy(coachEntries, 'pendingCount'),
        pastDueCount: sumBy(pastDueEntries, 'overdueCount'),
        taskCells: buildTaskCells(coachEntries),
        ...buildWorstOverdue(pastDueEntries),
      };
    })
    .sort((a, b) => (
      b.pastDueCount - a.pastDueCount
      || b.worstOverdueHours - a.worstOverdueHours
    ));
};

/**
 * Collapses queue entries into one summary per AC.
 *
 * Ordered by how many items are past due, then by the longest wait — the two things a lead said
 * they chase on. An AC who is clear still appears, so the list doubles as the roster.
 *
 * @param {Array} queueEntries - Queue entries from buildACQueueEntries, already filtered.
 * @returns {Array} One summary per AC.
 */
const buildACSummaries = (queueEntries) => {
  const entriesByACId = queueEntries.reduce((byAC, entry) => {
    const forAC = byAC.get(entry.acId) ?? [];
    forAC.push(entry);

    return byAC.set(entry.acId, forAC);
  }, new Map());

  return [...entriesByACId.values()].map((entries) => {
    const [{ acId, acName, acSlackId }] = entries;
    const pastDueEntries = entries.filter(({ isPastDue }) => isPastDue);

    return {
      acId,
      acName,
      acSlackId,
      coachCount: new Set(entries.map(({ coachId }) => coachId)).size,
      totalUnread: sumBy(entries, 'pendingCount'),
      pastDueCount: sumBy(pastDueEntries, 'overdueCount'),
      /*
       * Everything that has breached, filters or not, so the suspect share below has a denominator
       * that does not move when the lead changes what is in scope. An entry that breached is
       * exactly one that is either still in scope or excluded from it.
       */
      breachedCount: sumBy(entries.filter((entry) => entry.isPastDue || entry.isExcluded), 'overdueCount'),
      // The suspect mass is a property of the data rather than of the filter, so it is counted whole
      // — including when the lead has just switched it out of scope, which is when they most want
      // to know how big it is.
      suspectCount: sumBy(entries.filter((entry) => entry.isSuspect), 'overdueCount'),
      ...buildWorstOverdue(pastDueEntries),
      completionStatus: getCompletionStatus(pastDueEntries),
      taskCells: buildTaskCells(entries),
      coachBreakdown: buildCoachBreakdown(entries),
      sharedCoachNames: getSharedCoachNames(entries),
      // The Slack rundown only ever lists what is actually late.
      pastDueEntries,
    };
  }).sort((a, b) => (
    b.pastDueCount - a.pastDueCount
    // Ties go to the longest wait past SLA, which is what the Overdue column now shows.
    || b.worstOverdueHours - a.worstOverdueHours
    || a.acName.localeCompare(b.acName)
  ));
};

/**
 * What a task column orders on: the section's past-due count that is actually in scope.
 *
 * An excluded cell still shows a number, so ordering on the displayed one would rank a row by work
 * the lead has just filtered out — and disagree with the `Past due` column beside it.
 */
const getCountedTaskCount = (summary, section) => {
  const cell = summary.taskCells.find((taskCell) => taskCell.section === section);

  return cell?.isCounted ? cell.count ?? 0 : 0;
};

/** Ranks the grades so ascending reads clear → at risk → behind. */
const COMPLETION_RANK = {
  [CompletionStatus.CLEAR]: 0,
  [CompletionStatus.AT_RISK]: 1,
  [CompletionStatus.BEHIND]: 2,
};

/**
 * How each sortable column orders two ACs, ascending.
 *
 * Keyed by the column's field, and the set of keys here is what makes a column sortable at all —
 * `actions` is absent because it has no order to put it in. The seven task columns are added below,
 * each ordering on its own past-due count.
 */
const SUMMARY_COMPARATORS = {
  acName: (a, b) => a.acName.localeCompare(b.acName),
  coachCount: (a, b) => a.coachCount - b.coachCount,
  totalUnread: (a, b) => a.totalUnread - b.totalUnread,
  pastDueCount: (a, b) => a.pastDueCount - b.pastDueCount,
  worstOverdueHours: (a, b) => a.worstOverdueHours - b.worstOverdueHours,
  completionStatus: (a, b) => COMPLETION_RANK[a.completionStatus] - COMPLETION_RANK[b.completionStatus],
  ...SECTION_ORDER.reduce((comparators, section) => {
    const getCount = (summary) => getCountedTaskCount(summary, section);

    return { ...comparators, [section]: (a, b) => getCount(a) - getCount(b) };
  }, {}),
};

/**
 * The sortable fields a coach row carries too, so an expanded breakdown follows the column its AC
 * was ordered by instead of keeping an order that no longer matches the header.
 *
 * The rest are AC-level: a coach row has no `acName`, no `coachCount` of its own and no grade.
 */
const COACH_SORTABLE_FIELDS = new Set([
  'totalUnread',
  'pastDueCount',
  'worstOverdueHours',
  ...SECTION_ORDER,
]);

/** An AC with nothing past due has no worst wait to order by. */
const hasNoSortValue = (summary, field) => field === 'worstOverdueHours' && !summary.hasWorstOverdue;

/**
 * Reorders the roster for a grid sort model, keeping each AC's coaches with them.
 *
 * The grid is told not to sort its own rows (`sortingMode="server"`), because the rows are flat but
 * grouped — reordering them directly would strip coach rows away from their AC and leave orphaned
 * detail under whoever happened to land above them. Sorting the summaries and letting
 * `buildACLeadRows` re-flatten keeps the grouping intact by construction.
 *
 * An unsorted or unsortable model returns the input untouched, which is the default order
 * `buildACSummaries` established: most past due first, ties broken by the longest wait.
 *
 * @param {Array} summaries - Summaries from buildACSummaries.
 * @param {Array} sortModel - The grid's sort model; only its first entry is honoured.
 * @returns {Array} The summaries in the requested order.
 */
const sortACSummaries = (summaries, sortModel = []) => {
  const [{ field, sort } = {}] = sortModel;
  const compare = SUMMARY_COMPARATORS[field];

  if (!compare || !sort) {
    return summaries;
  }

  const direction = sort === 'desc' ? -1 : 1;
  /*
   * Rows with nothing past due sort last whichever way the column points. The list exists to
   * surface who is behind, so reversing a column must not promote everyone with nothing to chase.
   */
  const compareWithEmptyLast = (a, b) => (
    Number(hasNoSortValue(a, field)) - Number(hasNoSortValue(b, field))
    || direction * compare(a, b)
  );

  const sorted = [...summaries].sort((a, b) => (
    compareWithEmptyLast(a, b)
    // Equal values keep a readable order rather than whatever the input happened to be.
    || a.acName.localeCompare(b.acName)
  ));

  if (!COACH_SORTABLE_FIELDS.has(field)) {
    return sorted;
  }

  return sorted.map((summary) => ({
    ...summary,
    coachBreakdown: [...summary.coachBreakdown].sort((a, b) => (
      compareWithEmptyLast(a, b) || a.coachName.localeCompare(b.coachName)
    )),
  }));
};

/**
 * Flattens the summaries into grid rows: one per AC, with their coach breakdown underneath when
 * expanded. A lead looking at thirty people opens on who is behind, not on every queue.
 *
 * Each row carries its own task cells, so what is late on a coach — and a link to each of it — is
 * readable without leaving the page.
 */
const buildACLeadRows = (summaries, expandedACIds = new Set()) => summaries.flatMap((summary) => {
  const acRow = {
    ...summary, id: summary.acId, rowType: ACLeadRowType.AC, summary,
  };

  if (!expandedACIds.has(summary.acId)) {
    return [acRow];
  }

  return [
    acRow,
    ...summary.coachBreakdown.map((line) => ({
      ...line,
      id: `${summary.acId}:${line.coachId}`,
      rowType: ACLeadRowType.COACH,
      acId: summary.acId,
    })),
  ];
});

/**
 * Headline numbers for the roster. Counts people as well as items: thirty items spread across four
 * ACs and thirty sitting with one are very different problems.
 *
 * `suspectCount` and `breachedCount` are the two the filters do not move, so the suspect tile can
 * report a share of the whole backlog while the tile beside it reports only what is in scope.
 */
const buildACLeadStats = (summaries) => summaries.reduce((stats, summary) => {
  // The longest wait anywhere is one specific item, so its section and start come from whichever
  // summary holds the largest duration rather than being maxed independently.
  const isWorstOverdue = summary.hasWorstOverdue && summary.worstOverdueHours > stats.worstOverdueHours;

  return {
    acCount: stats.acCount + 1,
    coachAssignmentCount: stats.coachAssignmentCount + summary.coachCount,
    itemsPastDue: stats.itemsPastDue + summary.pastDueCount,
    breachedCount: stats.breachedCount + summary.breachedCount,
    suspectCount: stats.suspectCount + summary.suspectCount,
    acsNeedingAttention: stats.acsNeedingAttention + (summary.completionStatus === CompletionStatus.CLEAR ? 0 : 1),
    worstOverdueHours: isWorstOverdue ? summary.worstOverdueHours : stats.worstOverdueHours,
    worstOverdueSince: isWorstOverdue ? summary.worstOverdueSince : stats.worstOverdueSince,
    hasWorstOverdue: stats.hasWorstOverdue || summary.hasWorstOverdue,
  };
}, {
  acCount: 0,
  coachAssignmentCount: 0,
  itemsPastDue: 0,
  breachedCount: 0,
  suspectCount: 0,
  acsNeedingAttention: 0,
  worstOverdueHours: 0,
  worstOverdueSince: undefined,
  hasWorstOverdue: false,
});

/**
 * Deep link that opens a Slack DM with someone.
 *
 * `app_redirect` hands off to the desktop app when it is installed and falls back to the browser
 * otherwise, and unlike the `slack://` scheme it needs no workspace id.
 */
const getSlackDirectMessageUrl = (slackId) => `https://slack.com/app_redirect?channel=${slackId}`;

/**
 * The message a lead sends an AC about everything they have outstanding.
 *
 * Slack has no way to prefill a direct message from a link, so this is written to the clipboard for
 * the lead to paste. Links are absolute, since a relative path is meaningless once pasted out.
 */
const buildACSlackMessage = (summary, origin = window.location.origin) => [
  // Items, not queues — the count and the lines below it are different units, so say which.
  `Hi ${summary.acName} — ${summary.pastDueCount} overdue item(s):`,
  ...summary.pastDueEntries.map((entry) => [
    `• ${entry.coachName}`,
    entry.podName ? ` [${entry.podName}]` : '',
    // Overdue, not pending: the header totals overdue items, so a queue that is only partly late
    // would otherwise make the lines add up to more than the total they sit under.
    ` · ${entry.taskName} (${entry.overdueCount})`,
    ` · ${formatOverdueDuration(entry.overdueBusinessHours)} overdue`,
    ` · ${origin}${entry.path}`,
  ].join('')),
].join('\n');

export {
  ACLeadRowType,
  CompletionStatus,
  OverdueSeverity,
  PAST_DUE_THRESHOLD_HOURS,
  SECTION_ORDER,
  SUSPECT_THRESHOLD_BUSINESS_HOURS,
  SUSPECT_THRESHOLD_DAYS,
  TaskCellState,
  applyPastDueFilters,
  buildACFilterOptions,
  buildACLeadRows,
  buildACLeadStats,
  buildACQueueEntries,
  buildACSlackMessage,
  buildACSummaries,
  formatOverdueDuration,
  formatDashboardCount,
  formatThresholdLabel,
  getOverdueSeverity,
  getSlackDirectMessageUrl,
  sortACSummaries,
};
